"use client";

import React, { useState } from "react";
import { X, Trash2, Heart } from "lucide-react";
import { useCart } from "@/packages/context/CartContext";
import { VariantAttribute, Variant } from "@/packages/types/products";
import Price from "./Price";
import QuantitySelector from "./QuantitySelector";
import Badge from "./Badge";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "./NextImage";
import ConfirmDialog from "./ConfirmDialog";
import { useFreeShippingThreshold } from "@/packages/hooks/useFreeShippingThreshold";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useWishlist } from "@/packages/context/wishlistContext";
import { useAuth } from "@/packages/context/AuthContext";
import { toast } from "sonner";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";

export default function SideCartV2({ onClose }: { onClose: () => void }) {
  const { cartItems, updateCart, removeCart, clearCart } = useCart();
  const { fastPush } = useFastRouter();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isLoggedIn } = useAuth();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number | string; variant?: Variant } | null>(null);
  const { symbol, code, exchangeRate } = useCurrency();
  const freeShippingThreshold = useFreeShippingThreshold();

  const updateQty = (
    id: number | string,
    quantity: number,
    variantId: number | undefined
  ) => {
    const item = cartItems.find(
      (i) => i.id === id && i.variant?.id === variantId
    );
    if (!item) return;
    updateCart(item.id, quantity, item.variant);
  };

  const removeItem = (id: number | string, variantId: number | undefined) => {
    const item = cartItems.find(
      (i) => i.id == id && i.variant?.id == variantId
    );
    if (!item) return;

    setItemToDelete({ id: item.id, variant: item.variant });
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      removeCart(itemToDelete.id, itemToDelete.variant);
    }
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
    setItemToDelete(null);
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.quantity * (item.finalPrice ?? item.price),
    0
  );

  // Free shipping progress calculations (comparing converted subtotal vs currency threshold)
  const subtotalConverted = subtotal * exchangeRate;
  const thresholdAmount = freeShippingThreshold ?? 0;
  const neededForFreeShipping = Math.max(0, thresholdAmount - subtotalConverted);
  const shippingProgressPct = thresholdAmount > 0 
    ? Math.min(100, Math.round((subtotalConverted / thresholdAmount) * 100)) 
    : 100;

  return (
    <div className="flex flex-col h-full w-full bg-white shadow-2xl p-5 md:p-6 text-slate-800 font-sans">
      {/* Header */}
      <div className="flex justify-between items-start pb-3 border-b border-slate-100 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Shopping Cart</h2>
          <p className="text-xs text-slate-500 font-medium">
            {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close cart"
          className="p-1 rounded-full text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Free Shipping Progress Bar */}
      {freeShippingThreshold !== null && cartItems.length > 0 && (
        <div className="my-4 p-4 sm:p-5 bg-[#F4FBF6] border border-[#E2F4E8] rounded-[12px] space-y-3 shrink-0">
          {/* Primary Message */}
          <p className="text-[15px] font-semibold text-slate-800 leading-snug">
            {neededForFreeShipping > 0 ? (
              <>
                You&apos;re only <span className="font-bold text-slate-900">{symbol}{neededForFreeShipping.toFixed(2)} {code.toUpperCase()}</span> away from <span className="text-[#16A34A] font-bold">FREE shipping</span>
              </>
            ) : (
              <>
                You qualify for <span className="text-[#16A34A] font-bold">FREE shipping</span>!
              </>
            )}
          </p>

          {/* Progress Bar Track */}
          <div className="w-full h-[6px] bg-[#E5E7EB] rounded-[3px] overflow-hidden">
            <div
              className="h-full bg-[#22C55E] transition-all duration-300 rounded-[3px]"
              style={{ width: `${shippingProgressPct}%` }}
            />
          </div>

          {/* Secondary Text */}
          <p className="text-[12px] text-slate-500 font-medium">
            Free shipping on orders over <span className="font-semibold text-slate-700">{symbol}{thresholdAmount.toFixed(2)} {code.toUpperCase()}</span>
          </p>
        </div>
      )}

      {/* Empty State */}
      {cartItems.length <= 0 && (
        <div className="flex-1 flex flex-col justify-center items-center text-center p-6 space-y-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-2">
            <X className="w-8 h-8 opacity-40" />
          </div>
          <h3 className="text-base font-bold text-slate-800">Your Cart is Empty</h3>
          <p className="text-xs text-slate-500 max-w-[220px]">
            Looks like you haven&apos;t added any items to your cart yet.
          </p>
          <button
            onClick={() => {
              onClose();
              fastPush("/");
            }}
            className="mt-2 bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white text-xs font-bold px-6 py-2.5 rounded-full shadow-md transition-colors cursor-pointer"
          >
            Continue Shopping
          </button>
        </div>
      )}

      {/* Cart Items List */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin">
        {cartItems.map((item) => {
          const isFree = String(item.id).includes("-free");

          const variantImage =
            item.variant && item.images
              ? item.images.find((img) => img.id == item.variant?.imageId)
              : null;

          const imageUrl =
            variantImage?.imageUrl ||
            item?.images?.[0]?.imageUrl ||
            "/assets/site/images/default.png";

          const variantAttributes =
            item.variant?.variantAttributes
              ?.map(
                (attr: VariantAttribute) => attr.attribute_value?.name || ""
              )
              .filter(Boolean)
              .join(" - ") || null;

          const stock = item.variant?.stock ?? item.stock ?? Infinity;
          const isStockAvailable = stock > 0;

          return (
            <div
              key={`${item.id}-${item.variant?.id ?? "no-variant"}`}
              className="p-3 bg-white border border-slate-100 rounded-xl flex gap-3 items-start shadow-xs hover:border-slate-200 transition-all"
            >
              {/* Product Image */}
              <div
                onClick={() => {
                  onClose();
                  fastPush(`/products/${item.slug}`);
                }}
                className="w-16 h-16 rounded-lg border border-slate-100 overflow-hidden shrink-0 cursor-pointer bg-slate-50"
              >
                <NextImage
                  src={imageUrl?.trim() || "/assets/site/images/default.png"}
                  alt={item.title}
                  width={64}
                  height={64}
                  className="w-full h-full object-contain p-1"
                />
              </div>

              {/* Product Details */}
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex justify-between items-start gap-2">
                  <h4
                    onClick={() => {
                      onClose();
                      fastPush(`/products/${item.slug}`);
                    }}
                    className="text-xs font-bold text-slate-800 line-clamp-2 cursor-pointer hover:text-[var(--brand-accent)] transition-colors leading-snug"
                  >
                    {item.title}
                  </h4>
                </div>

                {variantAttributes && (
                  <p className="text-[11px] text-slate-400 font-medium">{variantAttributes}</p>
                )}

                {/* Price & Unit Breakdown */}
                {!isFree && (
                  <div className="flex items-baseline gap-1 text-xs">
                    <Price
                      amount={Number(item.finalPrice ?? item.price)}
                      className="font-black text-slate-900"
                    />
                    <span className="text-[10px] text-slate-400 font-normal">
                      / unit · x{item.quantity}
                    </span>
                  </div>
                )}

                {/* Action Row: Quantity + Stock Badge + Wishlist + Delete */}
                <div className="flex items-center justify-between pt-1 gap-2 flex-wrap">
                  {!isFree ? (
                    <>
                      {/* Quantity Selector */}
                      {isStockAvailable ? (
                        <QuantitySelector
                          value={item.quantity}
                          min={1}
                          max={stock}
                          onChange={(newQty) =>
                            updateQty(item.id, newQty, item.variant?.id)
                          }
                          className="scale-90 origin-left border border-slate-200 rounded-lg"
                        />
                      ) : (
                        <Badge text="Out of stock" className="bg-rose-500 text-white text-[10px]" />
                      )}



                      <div className="flex items-center gap-1.5 ml-auto">
                        {/* Wishlist Button */}
                        <button
                          type="button"
                          onClick={() => {
                            if (!isLoggedIn) {
                              toast.info("Please sign in to manage wishlist");
                              return;
                            }
                            toggleWishlist(item);
                          }}
                          aria-label="Wishlist toggle"
                          className="p-1 text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
                        >
                          <Heart className={`w-4 h-4 ${isInWishlist(item.id) ? "text-rose-500 fill-current" : ""}`} />
                        </button>

                        {/* Trash Button */}
                        <button
                          type="button"
                          onClick={() => removeItem(item.id, item.variant?.id)}
                          aria-label={`Remove ${item.title}`}
                          className="p-1 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <span className="text-xs text-emerald-600 font-bold">
                      🎁 Free Item (Qty: {item.quantity})
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Financial Summary & Actions */}
      {cartItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 shrink-0">
          <div className="flex justify-between items-center text-sm font-bold text-slate-900">
            <span>Total</span>
            <Price amount={subtotal} className="text-base font-black text-slate-900" />
          </div>

          {/* Action Buttons */}
          <div className="space-y-2 pt-1">
            <button
              onClick={() => {
                onClose();
                fastPush("/checkout");
              }}
              className={`w-full ${HEADER_V2_DESIGN_SYSTEM.colors.primaryBg} hover:opacity-95 text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl shadow-md transition-all cursor-pointer`}
            >
              Proceed to Checkout
            </button>
            <button
              onClick={() => {
                onClose();
                fastPush("/cart");
              }}
              className={`w-full ${HEADER_V2_DESIGN_SYSTEM.colors.accentBg} ${HEADER_V2_DESIGN_SYSTEM.colors.accentBgHover} text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl shadow-md transition-all cursor-pointer`}
            >
              View Cart Page
            </button>
            <div className="text-center pt-1">
              <button
                onClick={() => clearCart()}
                className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 underline cursor-pointer transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Remove Item"
        message="Are you sure you want to remove this item from your cart?"
        confirmLabel="Yes, remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
